import LoadingCard from "../../../common/LoadingCard";
import FolderIcon from "../../../common/FolderIcon";
import CustomItemDeleteButton from "../items/CustomItemDeleteButton";

import { AddFBAItemButton } from "./AddFBAItemButton";
import DisplaySkuMaps from "../items/DisplaySkuMaps";
import DisplayUomMaps from "../items/DisplayUomMaps";

import { CartItem } from "../../../services/webservice";
import { parseClassNames } from "../../../utils/Parse";
import { TABLE_COLUMN_TEXT } from "../../../utils/styles";

interface WmsItemsContentProps {
  data: CartItem[];
  loading: boolean;
  integrationConfigId: string;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  refreshItemsList: () => void;
}

export default function ItemsContent({
  data,
  loading,
  integrationConfigId,
  selectedIds,
  setSelectedIds,
  refreshItemsList,
}: WmsItemsContentProps) {
  // This function will be triggered when a checkbox changes its state
  const selectItem = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = parseInt(event.target.value);

    // Check if "ids" contains "selectedIds"
    // If true, this checkbox is already checked
    // Otherwise, it is not selected yet
    if (selectedIds.includes(selectedId)) {
      const newIds = selectedIds.filter((id) => id !== selectedId);
      setSelectedIds(newIds);
    } else {
      const newIds = [...selectedIds];
      newIds.push(selectedId);
      setSelectedIds(newIds);
    }
  };

  if (loading)
    return (
      <tr>
        <td colSpan={6}>
          <div className="text-center py-10">
            <LoadingCard />
          </div>
        </td>
      </tr>
    );

  if (data.length === 0)
    return (
      <tr>
        <td colSpan={6}>
          <div className="text-center py-10">
            <FolderIcon />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No Cart Items
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding custom items or contact support to bulk add
              your FCP items.
            </p>
          </div>
        </td>
      </tr>
    );

  return (
    <>
      {data.map((item) => (
        <tr
          key={item.cachedCartItemId}
          className={
            selectedIds.includes(item.cachedCartItemId) ? "bg-gray-50" : ""
          }
        >
          <td className="relative w-12 px-6 sm:w-16 sm:px-8">
            {selectedIds.includes(item.cachedCartItemId) && (
              <div className="absolute inset-y-0 left-0 w-0.5 bg-primary-600"></div>
            )}

            <input
              type="checkbox"
              onChange={selectItem}
              value={item.cachedCartItemId}
              checked={selectedIds.includes(item.cachedCartItemId)}
              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500 sm:left-6"
            />
          </td>
          <td
            className={parseClassNames(
              TABLE_COLUMN_TEXT.base,
              "max-w-xs break-normal"
            )}
          >
            <div>
              <div className="text-sm font-medium">
                <div className="flex space-x-2">
                  <span
                    className={
                      selectedIds.includes(item.cachedCartItemId)
                        ? "text-primary-600"
                        : ""
                    }
                  >
                    {item.sku === "" ? '""' : item.sku}
                  </span>
                </div>
              </div>
              <div
                className={parseClassNames(
                  "text-sm text-gray-500",
                  selectedIds.includes(item.cachedCartItemId)
                    ? "text-primary-500"
                    : ""
                )}
              >
                {item.name}
              </div>
            </div>
          </td>
          <td className={TABLE_COLUMN_TEXT.base}>
            <span
              className={parseClassNames(
                item.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800",
                "px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              )}
            >
              {item.isActive ? "Active" : "Inactive"}
            </span>
          </td>
          <td
            className={parseClassNames(
              TABLE_COLUMN_TEXT.base,
              TABLE_COLUMN_TEXT.secondary
            )}
          >
            {item.isCustom ? "Custom" : "Cart"}
          </td>
          <td className={parseClassNames(TABLE_COLUMN_TEXT.base)}>
            <DisplaySkuMaps map={item.skuMap} />
            <DisplayUomMaps map={item.uomMap} />
          </td>
          <td className={parseClassNames(TABLE_COLUMN_TEXT.base)}>
            <div className="flex space-x-2 justify-end items-center">
              <AddFBAItemButton
                integrationConfigId={integrationConfigId}
                item={item}
              />
              {item.isCustom && (
                <CustomItemDeleteButton
                  id={item.cachedCartItemId}
                  integrationConfigId={integrationConfigId}
                  refresh={refreshItemsList}
                />
              )}
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}
